import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  MenuItem,
  Snackbar,
  Alert,
  useTheme,
  Card,
  CardContent,
  Divider,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  marginBottom: theme.spacing(2),
  position: 'relative',
  '&:after': {
    content: '""',
    display: 'block',
    width: '80px',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(1),
  }
}));

const ContactInfoCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  borderRadius: theme.shape.borderRadius * 1.5,
  overflow: 'hidden',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(25, 118, 210, 0.08)' 
    : 'rgba(25, 118, 210, 0.04)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 12px 25px rgba(0, 0, 0, 0.4)' 
      : '0 12px 25px rgba(22, 56, 50, 0.15)',
  },
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: theme.shadows[3],
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[6],
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  '& .MuiSvgIcon-root': {
    fontSize: 44,
    color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(22, 56, 50, 0.1)',
    borderRadius: '50%',
    transition: 'all 0.3s ease',
  },
}));

const ContactMethod = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  '& .MuiSvgIcon-root': {
    fontSize: 24,
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(22, 56, 50, 0.1)',
    padding: theme.spacing(1),
    borderRadius: '50%',
  }
}));

const Contact = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    inquiryType: '',
  });
  
  // Add error state to track validation errors
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    subject: false,
    message: false,
    inquiryType: false,
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Add loading state
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error state when user types in a field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {
      name: !formValues.name.trim(),
      email: !formValues.email.trim() || !isValidEmail(formValues.email),
      subject: !formValues.subject.trim(),
      message: !formValues.message.trim(),
      inquiryType: !formValues.inquiryType
    };
    
    setErrors(newErrors);
    
    // Return true if no errors (all fields valid)
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before proceeding
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: t('contact.fillAllFields'),
        severity: 'error'
      });
      return;
    }
    
    // Set loading state
    setLoading(true);
    
    try {
      // Make API call to contact-lambda endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formValues),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        console.error('Contact form error:', data);
        throw new Error("Unable to send message. Please try again later.");
      }
      
      setSnackbar({
        open: true,
        message: data.message || t('contact.thankYouMessage'),
        severity: 'success'
      });
      
      // Reset form
      setFormValues({
        name: '',
        email: '',
        subject: '',
        message: '',
        inquiryType: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      setSnackbar({
        open: true,
        message: error.message || t('contact.sendError'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box 
        sx={{ 
          py: 10, 
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)'
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <SectionTitle variant="h3" align="center">
              {t('contact.title')}
            </SectionTitle>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              {t('contact.subtitle')}
            </Typography>
          </Box>
          <Typography variant="body1" align="center">
            {t('contact.description')}
          </Typography>
        </Container>
      </Box>

      {/* Contact Form and Info Section */}
      <Container maxWidth="xl">
        <Box sx={{ py: 8 }}>
          <Grid container spacing={4} alignItems="stretch">
            {/* Contact Info Card */}
            <Grid item xs={12} md={5}>
              <ContactInfoCard elevation={2}>
                <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <IconWrapper>
                    <ConnectWithoutContactIcon fontSize="large" />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {t('contact.getInTouch')}
                    </Typography>
                  </IconWrapper>
                  
                  <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                    {t('contact.contactDescription')}
                  </Typography>
                  
                  <Box sx={{ flexGrow: 1 }}>
                    <ContactMethod>
                      <EmailIcon />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('contact.email')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          info@digisparkfoundation.org
                        </Typography>
                      </Box>
                    </ContactMethod>
                
                  </Box>
                  
                  <Box sx={{ mt: 'auto', pt: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {t('contact.responseTime')}
                    </Typography>
                  </Box>
                </CardContent>
              </ContactInfoCard>
            </Grid>
            
            {/* Contact Form */}
            <Grid item xs={12} md={7}>
              <FormPaper elevation={3}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                  {t('contact.sendUsMessage')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {t('contact.formDescription')}
                </Typography>
                
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label={t('contact.name')}
                        name="name"
                        value={formValues.name}
                        onChange={handleChange}
                        error={errors.name}
                        helperText={errors.name ? t('contact.nameRequired') : ""}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label={t('contact.email')}
                        name="email"
                        type="email"
                        value={formValues.email}
                        onChange={handleChange}
                        error={errors.email}
                        helperText={errors.email ? t('contact.emailRequired') : ""}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        select
                        required
                        fullWidth
                        label={t('contact.inquiryType')}
                        name="inquiryType"
                        value={formValues.inquiryType}
                        onChange={handleChange}
                        error={errors.inquiryType}
                        helperText={errors.inquiryType ? t('contact.inquiryTypeRequired') : ""}
                      >
                        <MenuItem value="general">{t('contact.generalQuestion')}</MenuItem>
                        <MenuItem value="donation">{t('contact.donationInquiry')}</MenuItem>
                        <MenuItem value="partnership">{t('contact.partnershipOpportunity')}</MenuItem>
                        <MenuItem value="volunteer">{t('contact.volunteerInterest')}</MenuItem>
                        <MenuItem value="media">{t('contact.mediaInquiry')}</MenuItem>
                      </TextField>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label={t('contact.subject')}
                        name="subject"
                        value={formValues.subject}
                        onChange={handleChange}
                        error={errors.subject}
                        helperText={errors.subject ? t('contact.subjectRequired') : ""}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        multiline
                        rows={4}
                        label={t('contact.message')}
                        name="message"
                        value={formValues.message}
                        onChange={handleChange}
                        error={errors.message}
                        helperText={errors.message ? t('contact.messageRequired') : ""}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        size="large"
                        endIcon={loading ? undefined : <SendIcon />}
                        sx={{ 
                          mt: 2,
                          py: 1.5,
                          fontWeight: 700
                        }}
                        disabled={loading}
                      >
                        {loading ? (
                          <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                          t('contact.send')
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </FormPaper>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* FAQ Section */}
      <Box 
        sx={{ 
          py: 10, 
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)'
        }}
      >
        <Container maxWidth="md">
          <SectionTitle variant="h4" align="center" sx={{ mb: 6 }}>
            {t('contact.faqTitle')}
          </SectionTitle>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  {t('contact.faq1Question')}
                </Typography>
                <Typography variant="body1">
                  {t('contact.faq1Answer')}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  {t('contact.faq2Question')}
                </Typography>
                <Typography variant="body1">
                  {t('contact.faq2Answer')}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  {t('contact.faq3Question')}
                </Typography>
                <Typography variant="body1">
                  {t('contact.faq3Answer')}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  {t('contact.faq4Question')}
                </Typography>
                <Typography variant="body1">
                  {t('contact.faq4Answer')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;